// Admin → FAQ. The questions on gaintplay.com/faq, which are also what Claude's support assistant
// answers from. Answers start as drafts and go live when someone publishes them; topics and answers
// keep the order set here. "Preview" shows the page as users see it.
// /faq/from-ticket/:id is the review screen for "Turn into FAQ" on a support ticket: Claude's
// proposals, editable, saved or published only by a person.
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Bot,
  Check,
  CircleAlert,
  MessageCircleQuestion,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import {
  FAQ_LIMITS,
  faqLinkProblem,
  faqSlugFrom,
  faqSlugProblem,
  faqTextProblem,
  tidyFaqText,
} from '@gaintplay/core/faq';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  actorLabel,
  Card,
  confirmAction,
  Drawer,
  EmptyState,
  ErrorNote,
  PageHeader,
  Pill,
  run,
  SkeletonRows,
  Tabs,
  TimeAgo,
  toast,
} from '../components/ui';
import { Field, Segmented } from '../components/WallForm';

/** Where users read the FAQ. */
const SITE = 'https://gaintplay.com';

type Status = 'draft' | 'published';
interface Item {
  id: string;
  slug: string;
  groupId: string;
  question: string;
  answer: string;
  link: { to: string; label: string } | null;
  status: Status;
  sort: number;
  /** Goes up with every save; a save or delete names the version it was made from. */
  version: number;
  /** A draft of new wording for this answer: it replaces it, it's never published beside it. */
  replacesId: string | null;
  /** How many of the site's other languages have this answer as it reads now (published only). */
  translations: number;
  createdBy: string;
  createdAt: number;
  updatedBy: string;
  updatedAt: number;
}
interface Group {
  id: string;
  slug: string;
  title: string;
  sort: number;
  version: number;
  updatedBy: string;
  updatedAt: number;
  items: Item[];
}
interface FaqData {
  groups: Group[];
  staffNames: Record<string, string>;
  max: {
    groups: number;
    items: number;
  };
  canDraft: boolean;
  /** The live languages besides English: each published answer is translated into them. */
  languages: number;
}

const faqQuery = { queryKey: ['faq'], queryFn: () => api<FaqData>('/faq') } as const;

/* ------------------------------------------------------------------ The answer form */

interface Form {
  groupId: string;
  question: string;
  answer: string;
  slug: string;
  linkTo: string;
  linkLabel: string;
}
const formOf = (it: Item): Form => ({
  groupId: it.groupId,
  question: it.question,
  answer: it.answer,
  slug: it.slug,
  linkTo: it.link?.to ?? '',
  linkLabel: it.link?.label ?? '',
});
const hasLink = (f: Form) => !!(f.linkTo.trim() || f.linkLabel.trim());

/** The same rules the API applies (packages/core/src/faq.ts), field by field. */
function problems(f: Form) {
  return {
    question: faqTextProblem(tidyFaqText(f.question), 'question'),
    answer: faqTextProblem(tidyFaqText(f.answer), 'answer'),
    slug: faqSlugProblem(f.slug.trim()),
    link: hasLink(f)
      ? (faqLinkProblem(f.linkTo.trim()) ?? faqTextProblem(tidyFaqText(f.linkLabel), 'linkLabel'))
      : null,
  };
}
const firstProblem = (f: Form) => Object.values(problems(f)).find((p) => p) ?? null;

/** What the API takes for an answer. */
const payload = (f: Form) => ({
  groupId: f.groupId,
  slug: f.slug.trim(),
  question: f.question,
  answer: f.answer,
  link: hasLink(f) ? { to: f.linkTo.trim(), label: f.linkLabel } : null,
});

function Counter({ value, max }: { value: string; max: number }) {
  const n = tidyFaqText(value).length;
  return (
    <span className={`tabular-nums ${n > max ? 'font-semibold text-berry-deep' : 'text-stone'}`}>
      {n}/{max}
    </span>
  );
}

function Problem({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p className="mt-1.5 flex items-start gap-1.5 text-[12px] font-semibold leading-snug text-berry-deep">
      <CircleAlert size={13} className="mt-px shrink-0" aria-hidden="true" />
      {text}
    </p>
  );
}

/**
 * Topic, question, answer, anchor and optional link. `autoSlug`: the anchor follows the question
 * until someone edits it (new answers). `slugNote` explains what changing it does.
 */
function AnswerFields({
  form,
  set,
  groups,
  autoSlug,
  slugNote,
  slugLocked = false,
  showAll,
}: {
  form: Form;
  set: (f: Form) => void;
  groups: { id: string; title: string }[];
  autoSlug: boolean;
  slugNote?: string;
  slugLocked?: boolean;
  /** Show every problem (after a save was tried), not only those of fields already left. */
  showAll: boolean;
}) {
  const uid = useId();
  const [left, setLeft] = useState<Set<string>>(new Set());
  const p = problems(form);
  const show = (k: keyof typeof p) => (showAll || left.has(k) ? p[k] : null);
  const blur = (k: string) => () => setLeft((s) => new Set(s).add(k));
  const update = (patch: Partial<Form>) => {
    const next = { ...form, ...patch };
    if (autoSlug && patch.question !== undefined) next.slug = faqSlugFrom(patch.question);
    set(next);
  };
  return (
    <div className="space-y-5">
      <Field id={`${uid}-g`} label="Topic">
        <select
          id={`${uid}-g`}
          className="input text-[15px]"
          value={form.groupId}
          onChange={(e) => update({ groupId: e.target.value })}
        >
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.title}
            </option>
          ))}
        </select>
      </Field>
      <div>
        <div className="mb-1.5 flex items-baseline justify-between gap-3 text-[13px]">
          <label htmlFor={`${uid}-q`} className="font-semibold text-graphite">
            Question
          </label>
          <Counter value={form.question} max={FAQ_LIMITS.question.max} />
        </div>
        <input
          id={`${uid}-q`}
          className="input font-semibold"
          value={form.question}
          placeholder="How would a user ask it?"
          onChange={(e) => update({ question: e.target.value })}
          onBlur={blur('question')}
          aria-invalid={!!show('question')}
        />
        <Problem text={show('question')} />
      </div>
      <div>
        <div className="mb-1.5 flex items-baseline justify-between gap-3 text-[13px]">
          <label htmlFor={`${uid}-a`} className="font-semibold text-graphite">
            Answer
          </label>
          <Counter value={form.answer} max={FAQ_LIMITS.answer.max} />
        </div>
        <textarea
          id={`${uid}-a`}
          className="input min-h-40 resize-y text-[15px] leading-relaxed"
          value={form.answer}
          onChange={(e) => update({ answer: e.target.value })}
          onBlur={blur('answer')}
          aria-invalid={!!show('answer')}
          aria-describedby={`${uid}-ah`}
        />
        <Problem text={show('answer')} />
        <p id={`${uid}-ah`} className="mt-1.5 text-[12px] leading-snug text-stone">
          One paragraph of plain text, for anyone with this question. Where a number is a setting
          that can change, say “right now”. Claude’s support assistant answers from this too.
        </p>
      </div>
      <div>
        <label
          htmlFor={`${uid}-s`}
          className="mb-1.5 block text-[13px] font-semibold text-graphite"
        >
          Anchor
        </label>
        <div className="flex items-stretch overflow-hidden rounded-[10px] border border-hairline bg-paper focus-within:border-ink">
          <span className="flex items-center border-r border-hairline bg-canvas px-3 font-mono text-[13px] text-stone">
            /faq#
          </span>
          <input
            id={`${uid}-s`}
            className="min-w-0 flex-1 bg-transparent px-3 py-3 font-mono text-[14px] outline-none disabled:text-stone"
            value={form.slug}
            disabled={slugLocked}
            spellCheck={false}
            onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
            onBlur={blur('slug')}
            aria-invalid={!!show('slug')}
            aria-describedby={slugNote ? `${uid}-sh` : undefined}
          />
        </div>
        <Problem text={show('slug')} />
        {slugNote && (
          <p id={`${uid}-sh`} className="mt-1.5 text-[12px] leading-snug text-stone">
            {slugNote}
          </p>
        )}
      </div>
      <fieldset>
        <legend className="mb-1.5 text-[13px] font-semibold text-graphite">
          Link after the answer <span className="font-normal text-stone">· optional</span>
        </legend>
        <div className="grid gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <input
            className="input font-mono text-[14px]"
            aria-label="Page on the site"
            placeholder="e.g. /gift-cards"
            value={form.linkTo}
            spellCheck={false}
            onChange={(e) => update({ linkTo: e.target.value })}
            onBlur={blur('link')}
          />
          <input
            className="input text-[14px]"
            aria-label="Link text"
            placeholder="e.g. How cash-outs work"
            value={form.linkLabel}
            onChange={(e) => update({ linkLabel: e.target.value })}
            onBlur={blur('link')}
          />
        </div>
        <Problem text={show('link')} />
      </fieldset>
    </div>
  );
}

/* ------------------------------------------------------------------ As users see it */

/** One answer the way /faq draws it (apps/web pages/info/faq-markup.ts), opened. */
function SiteAnswer({
  question,
  answer,
  link,
  draft = false,
  open = false,
}: {
  question: string;
  answer: string;
  link: { to: string; label: string } | null;
  draft?: boolean;
  open?: boolean;
}) {
  return (
    <details open={open} className="group border-b border-hairline">
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 rounded-[6px] py-4 [&::-webkit-details-marker]:hidden">
        <h3 className="flex flex-wrap items-center gap-2 text-[17px] font-semibold leading-snug">
          {question || <span className="text-stone">Your question</span>}
          {draft && <Pill tone="sun">Draft</Pill>}
        </h3>
        <Plus
          size={18}
          aria-hidden="true"
          className="shrink-0 text-graphite transition group-open:rotate-45"
        />
      </summary>
      <div className="max-w-[64ch] pb-5 pr-8">
        <p className="text-pretty text-[16px] leading-relaxed text-graphite">
          {answer || <span className="text-stone">The answer appears here.</span>}
        </p>
        {link && (
          <a
            href={SITE + link.to}
            target="_blank"
            rel="noreferrer"
            className="group/link mt-3 inline-flex min-h-10 items-center gap-1.5 text-[15px] font-semibold text-ink"
          >
            <span className="underline decoration-hairline decoration-2 underline-offset-4 transition group-hover/link:decoration-ink">
              {link.label}
            </span>
            <ArrowRight size={15} aria-hidden="true" />
          </a>
        )}
      </div>
    </details>
  );
}

/** Word-by-word changes from `a` to `b` (longest common run of words; spaces kept). */
function diffWords(a: string, b: string): { op: 'same' | 'del' | 'ins'; text: string }[] {
  const x = a.split(/(\s+)/);
  const y = b.split(/(\s+)/);
  const out: { op: 'same' | 'del' | 'ins'; text: string }[] = [];
  const push = (op: 'same' | 'del' | 'ins', text: string) => {
    const last = out.at(-1);
    if (last?.op === op) last.text += text;
    else out.push({ op, text });
  };
  // Answers are at most 1,000 characters, so the table stays small.
  const lcs = Array.from({ length: x.length + 1 }, () => new Uint16Array(y.length + 1));
  for (let i = x.length - 1; i >= 0; i--)
    for (let j = y.length - 1; j >= 0; j--)
      lcs[i]![j] =
        x[i] === y[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
  let i = 0;
  let j = 0;
  while (i < x.length && j < y.length) {
    if (x[i] === y[j]) {
      push('same', x[i]!);
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) push('del', x[i++]!);
    else push('ins', y[j++]!);
  }
  while (i < x.length) push('del', x[i++]!);
  while (j < y.length) push('ins', y[j++]!);
  return out;
}

/** What changes in an answer that's on the site: removed words struck through, new ones marked. */
function Changes({ from, to }: { from: { q: string; a: string }; to: { q: string; a: string } }) {
  const line = (a: string, b: string) =>
    diffWords(a, b).map((p, k, all) =>
      p.op === 'same' ? (
        <span key={k}>{p.text}</span>
      ) : p.op === 'del' ? (
        <span key={k}>
          <del className="rounded-[3px] bg-berry/10 text-berry-deep decoration-berry/60">
            {p.text}
          </del>
          {/* Words swapped for others: a space between what goes and what comes. */}
          {all[k + 1]?.op === 'ins' && ' '}
        </span>
      ) : (
        <ins key={k} className="rounded-[3px] bg-mint/15 text-mint-deep no-underline">
          {p.text}
        </ins>
      ),
    );
  return (
    <div className="space-y-1.5 text-[14px] leading-relaxed">
      <p className="font-semibold">{line(from.q, to.q)}</p>
      <p className="text-graphite">{line(from.a, to.a)}</p>
      <p className="pt-1 text-[12px] text-stone">
        <del className="rounded-[3px] bg-berry/10 text-berry-deep">Struck through</del>: taken out.{' '}
        <ins className="rounded-[3px] bg-mint/15 text-mint-deep no-underline">Marked</ins>: added.
      </p>
    </div>
  );
}

const Swatch = () => (
  <span aria-hidden="true" className="inline-block size-2 shrink-0 rounded-[2px] bg-coin" />
);

/** The whole page as users see it: published answers, drafts too when asked. */
function SitePreview({ groups, drafts }: { groups: Group[]; drafts: boolean }) {
  const shown = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => drafts || i.status === 'published'),
    }))
    .filter((g) => g.items.length);
  if (!shown.length)
    return (
      <div className="card">
        <EmptyState icon={MessageCircleQuestion} title="Nothing published yet">
          Publish an answer and it shows here, and on the site within a few minutes.
        </EmptyState>
      </div>
    );
  return (
    <div className="rounded-[20px] border border-hairline bg-canvas px-4 py-8 sm:px-8 sm:py-10">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-12">
        <nav aria-label="Preview topics" className="lg:sticky lg:top-6 lg:self-start">
          <h2 className="text-[13px] font-semibold text-stone">Topics</h2>
          <ul className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-0.5">
            {shown.map((g) => (
              <li key={g.id}>
                <a
                  href={`#preview-${g.slug}`}
                  className="flex min-h-10 items-center rounded-full border border-hairline bg-paper px-3.5 text-[14px] font-semibold text-graphite transition hover:border-ink hover:text-ink lg:rounded-[10px] lg:border-transparent lg:bg-transparent lg:px-3 lg:py-1.5 lg:hover:bg-paper"
                >
                  {g.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="min-w-0 space-y-12">
          {shown.map((g) => (
            <section key={g.id} id={`preview-${g.slug}`} className="scroll-mt-6">
              <h2 className="flex items-center gap-2.5 text-[24px] font-extrabold tracking-[-0.02em]">
                <Swatch /> {g.title}
              </h2>
              <div className="mt-4 border-t border-hairline">
                {g.items.map((it) => (
                  <SiteAnswer
                    key={it.id}
                    question={it.question}
                    answer={it.answer}
                    link={it.link}
                    draft={it.status === 'draft'}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ The FAQ page */

type Filter = 'all' | 'published' | 'draft';

export function Faq() {
  const { data, error, isLoading, refetch } = useQuery(faqQuery);
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'preview' ? 'preview' : 'edit';
  const filter = (['published', 'draft'] as const).find((f) => f === params.get('show')) ?? 'all';
  const setParam = (k: string, v: string | null) =>
    setParams((p) => (v ? p.set(k, v) : p.delete(k), p), { replace: true });
  const [withDrafts, setWithDrafts] = useState(false);
  const [editing, setEditing] = useState<Item | 'new' | null>(null);
  const [newTopic, setNewTopic] = useState(false);

  // ?item=<id> (from the review screen) opens that answer.
  const openId = params.get('item');
  useEffect(() => {
    if (!openId || !data) return;
    const it = data.groups.flatMap((g) => g.items).find((i) => i.id === openId);
    if (it) setEditing(it);
    setParam('item', null);
  }, [openId, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const all = data?.groups.flatMap((g) => g.items) ?? [];
  const counts = {
    published: all.filter((i) => i.status === 'published').length,
    draft: all.filter((i) => i.status === 'draft').length,
  };

  if (!can('faq.manage'))
    return (
      <div className="space-y-5">
        <PageHeader title="FAQ" />
        <ErrorNote
          error={new Error('Your role can’t edit the FAQ. An owner can give it access.')}
        />
      </div>
    );

  return (
    <div className="space-y-5">
      <PageHeader
        title="FAQ"
        description={
          <>
            What people read on{' '}
            <a
              href={`${SITE}/faq`}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink underline decoration-hairline decoration-2 underline-offset-2 hover:decoration-ink"
            >
              gaintplay.com/faq
            </a>{' '}
            and what Claude’s support assistant answers from. Drafts stay here; a published change
            reaches the site within a few minutes.
          </>
        }
        actions={
          data && (
            <>
              <button
                type="button"
                className="btn-ghost min-h-10 px-3.5 py-2 text-[14px]"
                onClick={() => setNewTopic(true)}
                disabled={data.groups.length >= data.max.groups}
              >
                <Plus size={15} aria-hidden="true" /> New topic
              </button>
              <button
                type="button"
                className="btn-primary min-h-10 px-3.5 py-2 text-[14px]"
                onClick={() => setEditing('new')}
                disabled={!data.groups.length || all.length >= data.max.items}
              >
                <Plus size={15} aria-hidden="true" /> New answer
              </button>
            </>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          label="View"
          value={view}
          onChange={(v) => setParam('view', v === 'preview' ? 'preview' : null)}
          options={[
            { key: 'edit', label: 'Edit' },
            { key: 'preview', label: 'Preview' },
          ]}
        />
        {view === 'edit' ? (
          <Tabs<Filter>
            label="Show"
            value={filter}
            onChange={(k) => setParam('show', k === 'all' ? null : k)}
            items={[
              { key: 'all', label: 'All', count: all.length },
              { key: 'published', label: 'Published', count: counts.published },
              { key: 'draft', label: 'Drafts', count: counts.draft, hot: counts.draft > 0 },
            ]}
          />
        ) : (
          <label className="flex min-h-10 items-center gap-2 text-[14px] text-graphite">
            <input
              type="checkbox"
              className="h-4 w-4 accent-ink"
              checked={withDrafts}
              onChange={(e) => setWithDrafts(e.target.checked)}
            />
            Show drafts too
          </label>
        )}
      </div>

      <ErrorNote error={error} onRetry={() => void refetch()} />
      {isLoading && (
        <div className="card">
          <SkeletonRows />
        </div>
      )}

      {data && newTopic && <TopicForm onDone={() => setNewTopic(false)} />}

      {data && view === 'preview' && <SitePreview groups={data.groups} drafts={withDrafts} />}

      {data && view === 'edit' && (
        <>
          {data.groups.length === 0 && !newTopic && (
            <div className="card">
              <EmptyState
                icon={MessageCircleQuestion}
                title="No topics yet"
                action={
                  <button type="button" className="btn-primary" onClick={() => setNewTopic(true)}>
                    <Plus size={15} aria-hidden="true" /> New topic
                  </button>
                }
              >
                Answers live in topics, like “Cashing out”. Add the first one.
              </EmptyState>
            </div>
          )}
          {filter !== 'all' && counts[filter] === 0 && data.groups.length > 0 && (
            <div className="card">
              <EmptyState
                icon={MessageCircleQuestion}
                title={filter === 'draft' ? 'No drafts' : 'Nothing published'}
              >
                {filter === 'draft'
                  ? 'Every answer is live. New answers and “Turn into FAQ” drafts wait here.'
                  : 'Nothing is on the site yet. Publish a draft to show it.'}
              </EmptyState>
            </div>
          )}
          {data.groups.map((g, i) => (
            <TopicCard
              key={g.id}
              group={g}
              groups={data.groups}
              index={i}
              filter={filter}
              names={data.staffNames}
              languages={data.languages}
              onOpen={setEditing}
            />
          ))}
        </>
      )}

      {data && editing && (
        <AnswerEditor
          key={editing === 'new' ? 'new' : editing.id}
          item={editing === 'new' ? null : editing}
          groups={data.groups}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

const StatusTag = ({ status }: { status: Status }) =>
  status === 'draft' ? <Pill tone="sun">Draft</Pill> : <Pill tone="mint">Published</Pill>;

/**
 * A published answer's translations (lib/faq-translate.ts): Claude translates it into every
 * language the site speaks a few minutes after it's published or changed; until then that
 * language shows the English.
 */
const LanguagesTag = ({ item, languages }: { item: Item; languages: number }) =>
  item.status !== 'published' || languages === 0 ? null : item.translations >= languages ? (
    <span title="Shown in every language the site speaks">
      <Pill tone="mint">In {languages + 1} languages</Pill>
    </span>
  ) : (
    <span title="Being translated: until then those languages show the English">
      <Pill tone="sun">
        Translating {item.translations}/{languages}
      </Pill>
    </span>
  );

/** Move one id up or down in a list of ids. */
const moved = (ids: string[], i: number, by: -1 | 1) => {
  const next = [...ids];
  const j = i + by;
  if (j < 0 || j >= next.length) return null;
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
};

function MoveButtons({
  label,
  onUp,
  onDown,
}: {
  label: string;
  onUp: (() => void) | null;
  onDown: (() => void) | null;
}) {
  const cls =
    'grid h-8 w-8 place-items-center rounded-[8px] text-stone transition hover:bg-canvas hover:text-ink disabled:pointer-events-none disabled:opacity-30 max-sm:h-10 max-sm:w-10';
  return (
    <span className="flex shrink-0 flex-col">
      <button
        type="button"
        className={cls}
        onClick={onUp ?? undefined}
        disabled={!onUp}
        aria-label={`Move ${label} up`}
        title="Move up"
      >
        <ArrowUp size={15} />
      </button>
      <button
        type="button"
        className={cls}
        onClick={onDown ?? undefined}
        disabled={!onDown}
        aria-label={`Move ${label} down`}
        title="Move down"
      >
        <ArrowDown size={15} />
      </button>
    </span>
  );
}

function TopicCard({
  group,
  groups,
  index,
  filter,
  names,
  languages,
  onOpen,
}: {
  group: Group;
  groups: Group[];
  index: number;
  filter: Filter;
  names: Record<string, string>;
  languages: number;
  onOpen: (it: Item) => void;
}) {
  const qc = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const items = group.items.filter((i) => filter === 'all' || i.status === filter);
  if (filter !== 'all' && !items.length) return null;
  const drafts = group.items.filter((i) => i.status === 'draft').length;
  const refresh = () => qc.invalidateQueries({ queryKey: ['faq'] });

  const moveTopic = (by: -1 | 1) => {
    const ids = moved(
      groups.map((g) => g.id),
      index,
      by,
    );
    return ids
      ? async () => {
          await run(() => api('/faq/groups/order', { body: { ids } }));
          await refresh();
        }
      : null;
  };
  const moveItem = (i: number, by: -1 | 1) => {
    const ids = moved(
      group.items.map((it) => it.id),
      i,
      by,
    );
    return ids
      ? async () => {
          await run(() => api('/faq/items/order', { body: { groupId: group.id, ids } }));
          await refresh();
        }
      : null;
  };
  const remove = async () => {
    const ok = await confirmAction({
      title: `Delete the topic “${group.title}”?`,
      body: 'It has no answers, so nothing on the site changes.',
      confirmLabel: 'Delete topic',
      tone: 'danger',
    });
    if (ok === null) return;
    await run(
      () => api(`/faq/groups/${group.id}`, { method: 'DELETE', body: { version: group.version } }),
      'Topic deleted.',
    );
    await refresh();
  };
  const answerById = (id: string | null) =>
    id ? groups.flatMap((g) => g.items).find((i) => i.id === id) : undefined;

  return (
    <section className="card" aria-labelledby={`topic-${group.id}`}>
      {renaming ? (
        <div className="border-b border-hairline p-4 sm:p-5">
          <TopicForm group={group} onDone={() => setRenaming(false)} />
        </div>
      ) : (
        <div className="flex min-h-[60px] items-center gap-2 border-b border-hairline py-2.5 pl-2 pr-3 sm:pr-4">
          {filter === 'all' ? (
            <MoveButtons label={group.title} onUp={moveTopic(-1)} onDown={moveTopic(1)} />
          ) : (
            <span className="w-2" />
          )}
          <div className="min-w-0 flex-1">
            <h2 id={`topic-${group.id}`} className="text-[16px] font-extrabold tracking-[-0.01em]">
              {group.title}
            </h2>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[12px] text-stone">
              <code className="font-mono">#{group.slug}</code>
              <span aria-hidden="true">·</span>
              <span>
                {group.items.length === 1 ? '1 answer' : `${group.items.length} answers`}
                {drafts > 0 && `, ${drafts} ${drafts === 1 ? 'draft' : 'drafts'}`}
              </span>
            </p>
          </div>
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-[10px] text-stone transition hover:bg-canvas hover:text-ink"
            onClick={() => setRenaming(true)}
            aria-label={`Rename ${group.title}`}
            title="Rename"
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-[10px] text-stone transition hover:bg-berry/10 hover:text-berry-deep disabled:pointer-events-none disabled:opacity-30"
            onClick={() => void remove()}
            disabled={group.items.length > 0}
            aria-label={`Delete ${group.title}`}
            title={group.items.length ? 'Move or delete its answers first' : 'Delete topic'}
          >
            <Trash2 size={15} />
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <p className="px-5 py-6 text-[14px] text-stone">No answers in this topic yet.</p>
      ) : (
        <ol className="divide-y divide-hairline">
          {items.map((it) => {
            const i = group.items.indexOf(it);
            return (
              <li key={it.id} className="flex items-start gap-2 py-2 pl-2 pr-3 sm:pr-4">
                {filter === 'all' ? (
                  <MoveButtons label={it.question} onUp={moveItem(i, -1)} onDown={moveItem(i, 1)} />
                ) : (
                  <span className="w-2" />
                )}
                <button
                  type="button"
                  onClick={() => onOpen(it)}
                  className="group min-w-0 flex-1 rounded-[10px] px-2 py-2 text-left transition hover:bg-[#fbf8f3]"
                >
                  <span className="flex items-start gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-bold leading-snug group-hover:underline group-hover:decoration-hairline group-hover:decoration-2 group-hover:underline-offset-2">
                        {it.question}
                      </span>
                      <span className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-graphite">
                        {it.answer}
                      </span>
                    </span>
                    <span className="hidden shrink-0 pt-0.5 sm:block">
                      <StatusTag status={it.status} />
                    </span>
                  </span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-stone">
                    <span className="sm:hidden">
                      <StatusTag status={it.status} />
                    </span>
                    <LanguagesTag item={it} languages={languages} />
                    {answerById(it.replacesId) && (
                      <Pill tone="sky">New wording for #{answerById(it.replacesId)!.slug}</Pill>
                    )}
                    <code className="font-mono">#{it.slug}</code>
                    <span aria-hidden="true">·</span>
                    <span>
                      Edited <TimeAgo t={it.updatedAt} /> by {actorLabel(it.updatedBy, names)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/** Add a topic, or rename one (its anchor too). */
function TopicForm({ group, onDone }: { group?: Group; onDone: () => void }) {
  const uid = useId();
  const qc = useQueryClient();
  const [title, setTitle] = useState(group?.title ?? '');
  const [slug, setSlug] = useState(group?.slug ?? '');
  const [touchedSlug, setTouchedSlug] = useState(!!group);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const shownSlug = touchedSlug ? slug : faqSlugFrom(title);
  const save = async () => {
    const problem = faqTextProblem(tidyFaqText(title), 'title') ?? faqSlugProblem(shownSlug.trim());
    if (problem) return setErr(problem);
    setSaving(true);
    setErr(null);
    try {
      if (group)
        await api(`/faq/groups/${group.id}`, {
          method: 'PATCH',
          body: { title, slug: shownSlug.trim(), version: group.version },
        });
      else await api('/faq/groups', { body: { title, slug: shownSlug.trim() } });
      await qc.invalidateQueries({ queryKey: ['faq'] });
      toast(group ? 'Topic saved.' : 'Topic added. Add its first answer.');
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const body = (
    <form
      className="grid gap-3 sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] sm:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div>
        <label htmlFor={`${uid}-t`} className="label">
          Topic name
        </label>
        <input
          id={`${uid}-t`}
          className="input"
          autoFocus
          value={title}
          placeholder="Cashing out"
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor={`${uid}-s`} className="label">
          Anchor
        </label>
        <input
          id={`${uid}-s`}
          className="input font-mono text-[14px]"
          value={shownSlug}
          spellCheck={false}
          onChange={(e) => {
            setTouchedSlug(true);
            setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
          }}
        />
      </div>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary h-12 py-0" disabled={saving}>
          {saving ? 'Saving…' : group ? 'Save' : 'Add topic'}
        </button>
        <button type="button" className="btn-ghost h-12 py-0" onClick={onDone}>
          Cancel
        </button>
      </div>
      {err && (
        <p role="alert" className="text-[13px] font-semibold text-berry-deep sm:col-span-3">
          {err}
        </p>
      )}
      {group && shownSlug !== group.slug && (
        <p className="text-[12px] text-stone sm:col-span-3">
          Links people saved to /faq#{group.slug} will stop jumping to this topic.
        </p>
      )}
    </form>
  );
  return group ? body : <Card title="New topic">{body}</Card>;
}

/** The drawer for one answer: edit, preview, publish or keep as a draft, delete. */
function AnswerEditor({
  item,
  groups,
  onClose,
}: {
  item: Item | null;
  groups: Group[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const initial: Form = useMemo(
    () =>
      item
        ? formOf(item)
        : {
            groupId: groups[0]?.id ?? '',
            question: '',
            answer: '',
            slug: '',
            linkTo: '',
            linkLabel: '',
          },
    [item, groups],
  );
  const [form, setForm] = useState(initial);
  const [tried, setTried] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const published = item?.status === 'published';
  // New wording for a live answer: it replaces that answer instead of being published beside it.
  const live = item?.replacesId
    ? groups.flatMap((g) => g.items).find((i) => i.id === item.replacesId)
    : undefined;

  const close = async () => {
    if (
      dirty &&
      (await confirmAction({
        title: 'Discard your changes?',
        body: 'Nothing you changed here has been saved.',
        confirmLabel: 'Discard',
        tone: 'danger',
      })) === null
    )
      return;
    onClose();
  };

  const save = async (status: Status) => {
    setTried(true);
    const problem = firstProblem(form);
    if (problem) return setErr(problem);
    setErr(null);
    setBusy(true);
    try {
      if (item)
        await api(`/faq/items/${item.id}`, {
          method: 'PATCH',
          body: { ...payload(form), status, version: item.version },
        });
      else await api('/faq/items', { body: { ...payload(form), status } });
      await qc.invalidateQueries({ queryKey: ['faq'] });
      toast(
        status === 'published'
          ? published
            ? 'Saved. The site shows it within a few minutes, and in every language soon after.'
            : 'Published. The site shows it within a few minutes, and in every language soon after.'
          : published
            ? 'Moved to drafts. It leaves the site within a few minutes.'
            : 'Saved as a draft.',
      );
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** Put this draft's wording into the live answer (saving any edits first); the draft goes. */
  const replaceLive = async () => {
    if (!item || !live) return;
    setTried(true);
    const problem = firstProblem(form);
    if (problem) return setErr(problem);
    const ok = await confirmAction({
      title: 'Replace the live answer?',
      body: `“${live.question}” gets this wording, under its anchor #${live.slug}. The site and the support assistant use it within a few minutes, and this draft is gone.`,
      confirmLabel: 'Replace',
    });
    if (ok === null) return;
    setErr(null);
    setBusy(true);
    try {
      let version = item.version;
      if (dirty)
        version = (
          await api<{ item: Item }>(`/faq/items/${item.id}`, {
            method: 'PATCH',
            body: { ...payload(form), status: 'draft', version },
          })
        ).item.version;
      await api(`/faq/items/${item.id}/replace`, { body: { version } });
      await qc.invalidateQueries({ queryKey: ['faq'] });
      toast('Replaced. The site shows the new wording within a few minutes.');
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    const ok = await confirmAction({
      title: 'Delete this answer?',
      body: published
        ? 'It leaves the site and the support assistant within a few minutes. The audit log keeps a copy.'
        : 'The draft is gone for good. The audit log keeps a copy.',
      confirmLabel: 'Delete answer',
      tone: 'danger',
    });
    if (ok === null) return;
    setBusy(true);
    const done = await run(
      () => api(`/faq/items/${item.id}`, { method: 'DELETE', body: { version: item.version } }),
      'Answer deleted.',
    );
    setBusy(false);
    if (done === undefined) return;
    await qc.invalidateQueries({ queryKey: ['faq'] });
    onClose();
  };

  return (
    <Drawer
      onClose={() => void close()}
      title={item ? item.question : 'New answer'}
      subtitle={
        <span className="inline-flex flex-wrap items-center gap-2">
          {item && <StatusTag status={item.status} />}
          <span className="font-mono text-[12px]">/faq#{form.slug || '…'}</span>
        </span>
      }
      footer={
        <div className="space-y-2.5">
          {err && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-[10px] bg-berry/[0.06] px-3 py-2 text-[13px] font-semibold text-berry-deep"
            >
              <CircleAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
              {err}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {live ? (
              <button
                type="button"
                className="btn-primary h-11 py-0"
                disabled={busy}
                onClick={() => void replaceLive()}
              >
                Replace the live answer
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary h-11 py-0"
                disabled={busy || (published && !dirty)}
                onClick={() => void save('published')}
              >
                {published ? 'Save changes' : 'Publish'}
              </button>
            )}
            <button
              type="button"
              className="btn-ghost h-11 py-0"
              disabled={busy || (!!item && !published && !dirty)}
              onClick={() => void save('draft')}
            >
              {published ? 'Move to drafts' : 'Save draft'}
            </button>
            {dirty && (
              <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-graphite">
                <span className="h-2 w-2 rounded-full bg-ink" aria-hidden="true" />
                Unsaved changes
              </span>
            )}
            {item && (
              <button
                type="button"
                className="ml-auto inline-flex h-11 items-center gap-1.5 rounded-[10px] px-3 text-[14px] font-semibold text-berry-deep transition hover:bg-berry/10"
                onClick={() => void remove()}
                disabled={busy}
              >
                <Trash2 size={15} aria-hidden="true" /> Delete
              </button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-8">
        {live && (
          <section
            aria-labelledby="faq-live-changes"
            className="rounded-[14px] border border-hairline bg-paper p-4"
          >
            <h3 id="faq-live-changes" className="mb-2 text-[13px] font-semibold text-graphite">
              New wording for the answer on the site (#{live.slug})
            </h3>
            <Changes
              from={{ q: live.question, a: live.answer }}
              to={{ q: tidyFaqText(form.question), a: tidyFaqText(form.answer) }}
            />
          </section>
        )}
        <AnswerFields
          form={form}
          set={setForm}
          groups={groups}
          autoSlug={!item && !touchedSlug(form, initial)}
          {...(published
            ? { slugNote: 'Changing it breaks links people saved to this answer.' }
            : live
              ? { slugNote: 'Only this draft’s. The answer keeps its own when you replace it.' }
              : {})}
          showAll={tried}
        />
        <section aria-label="Preview">
          <h3 className="mb-2 text-[13px] font-semibold text-graphite">On the site</h3>
          <div className="rounded-[14px] border border-hairline bg-canvas px-4">
            <SiteAnswer
              open
              question={tidyFaqText(form.question)}
              answer={tidyFaqText(form.answer)}
              link={
                hasLink(form) && !problems(form).link
                  ? { to: form.linkTo.trim(), label: tidyFaqText(form.linkLabel) }
                  : null
              }
            />
          </div>
        </section>
        {item && (
          <p className="text-[12px] text-stone">
            Added <TimeAgo t={item.createdAt} />, last edited <TimeAgo t={item.updatedAt} />. Every
            change is in the audit log.
          </p>
        )}
      </div>
    </Drawer>
  );
}

/** The anchor follows the question only while it still is the one made from the question. */
const touchedSlug = (f: Form, initial: Form) =>
  f.slug !== initial.slug && f.slug !== faqSlugFrom(f.question);

/* ------------------------------------------------------------------ Turn into FAQ */

interface Draft {
  kind: 'new' | 'edit';
  target: Item | null;
  groupId: string;
  slug: string;
  question: string;
  answer: string;
  reason: string;
  warnings: string[];
}
interface Drafts {
  ticket: { id: string; subject: string | null };
  drafts: Draft[];
  summary: string;
}
interface TicketMsg {
  id: string;
  author: string;
  body: string;
  english: string | null;
  createdAt: number;
}

export function FaqFromTicket() {
  const { id } = useParams();
  const qc = useQueryClient();
  const can = useCan();
  // One model call per visit; "Ask again" calls it again.
  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ['faq-drafts', id],
    queryFn: () => api<Drafts>(`/faq/from-ticket/${id}`, { body: {} }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const faq = useQuery(faqQuery);
  const ticket = useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () =>
      api<{
        messages: TicketMsg[];
        staffNames: Record<string, string>;
        ticket: { username: string | null };
      }>(`/support/${id}`),
  });

  const back = (
    <Link
      to={`/support/${id}`}
      className="-my-2 -ml-1 inline-flex min-h-10 items-center gap-1 rounded-[8px] px-1 text-[13px] font-semibold text-stone transition hover:text-ink"
    >
      <ArrowLeft size={14} /> Ticket
    </Link>
  );
  if (!can('faq.manage'))
    return (
      <div className="space-y-5">
        {back}
        <ErrorNote
          error={new Error('Your role can’t edit the FAQ. An owner can give it access.')}
        />
      </div>
    );

  return (
    <div className="space-y-5">
      {back}
      <PageHeader
        eyebrow="Turn into FAQ"
        title={data?.ticket.subject ?? 'FAQ drafts from a ticket'}
        description="Claude read this conversation and the current FAQ, and suggests answers that would help anyone with the same question. Nothing is saved until you save it, and nothing reaches the site until you publish it."
        actions={
          <button
            type="button"
            className="btn-ghost min-h-10 px-3.5 py-2 text-[14px]"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            <RotateCcw size={15} aria-hidden="true" /> Ask again
          </button>
        }
      />
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4">
          {isFetching && (
            <div className="card p-5" role="status" aria-live="polite">
              <p className="flex items-center gap-2 text-[14px] font-semibold text-graphite">
                <Bot size={16} aria-hidden="true" className="animate-pulse" />
                Claude is reading the conversation and the FAQ. This takes up to half a minute.
              </p>
              <div className="mt-4 space-y-2" aria-hidden="true">
                <div className="h-4 w-2/3 animate-pulse rounded bg-ink/[0.07]" />
                <div className="h-3 w-full animate-pulse rounded bg-ink/[0.05]" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-ink/[0.05]" />
              </div>
            </div>
          )}
          {!isFetching && <ErrorNote error={error} onRetry={() => void refetch()} />}
          {data && !isFetching && (
            <>
              {data.summary && (
                <p className="flex items-start gap-2 text-[14px] leading-relaxed text-graphite">
                  <Bot size={16} className="mt-0.5 shrink-0 text-stone" aria-hidden="true" />
                  <span>
                    <span className="font-semibold text-ink">Claude: </span>
                    {data.summary}
                  </span>
                </p>
              )}
              {data.drafts.length === 0 ? (
                <div className="card">
                  <EmptyState
                    icon={MessageCircleQuestion}
                    title="Nothing for the FAQ in this one"
                    action={
                      <Link to={`/support/${id}`} className="btn-ghost">
                        Back to the ticket
                      </Link>
                    }
                  >
                    One-off problems and questions the FAQ already answers don’t need a new entry.
                  </EmptyState>
                </div>
              ) : (
                faq.data &&
                data.drafts.map((d, i) => (
                  <DraftCard
                    key={`${data.ticket.id}-${i}`}
                    n={i + 1}
                    of={data.drafts.length}
                    draft={d}
                    ticketId={data.ticket.id}
                    groups={faq.data.groups}
                    onSaved={() => void qc.invalidateQueries({ queryKey: ['faq'] })}
                  />
                ))
              )}
            </>
          )}
        </div>
        <aside aria-label="The conversation" className="lg:sticky lg:top-6">
          <Card title="The conversation" pad={false}>
            {!ticket.data ? (
              <SkeletonRows rows={3} />
            ) : (
              <ol className="max-h-[70vh] divide-y divide-hairline overflow-y-auto">
                {ticket.data.messages.map((m) => (
                  <li key={m.id} className="px-4 py-3 text-[13px] leading-relaxed">
                    <div className="mb-0.5 text-[12px] font-semibold text-stone">
                      {m.author.startsWith('usr_') || m.author.startsWith('email:')
                        ? (ticket.data.ticket.username ?? 'User')
                        : actorLabel(m.author, ticket.data.staffNames)}
                    </div>
                    <p className="whitespace-pre-wrap break-words text-graphite">
                      {m.english ?? m.body}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}

function DraftCard({
  n,
  of,
  draft,
  ticketId,
  groups,
  onSaved,
}: {
  n: number;
  of: number;
  draft: Draft;
  ticketId: string;
  groups: Group[];
  onSaved: () => void;
}) {
  const target = draft.target;
  const live = target?.status === 'published';
  const initial: Form = {
    groupId: draft.groupId,
    question: draft.question,
    answer: draft.answer,
    slug: draft.slug,
    linkTo: target?.link?.to ?? '',
    linkLabel: target?.link?.label ?? '',
  };
  const [form, setForm] = useState(initial);
  const [tried, setTried] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<
    { status: Status; id: string; replaces: boolean } | 'skipped' | null
  >(null);

  /**
   * Save: as a new answer; into the answer this edit is for (a draft, or publishing the change to
   * a live one); or, for a live answer, as a draft of new wording that replaces it later from the
   * FAQ page (the API gives that draft an anchor of its own).
   */
  const save = async (status: Status, into: 'new' | 'target' | 'new-wording') => {
    setTried(true);
    const problem = firstProblem(form);
    if (problem) return setErr(problem);
    if (
      status === 'published' &&
      (await confirmAction({
        title: into === 'target' ? 'Publish these changes?' : 'Publish this answer?',
        body: 'It shows on gaintplay.com/faq, and Claude’s support assistant answers from it, within a few minutes.',
        confirmLabel: 'Publish',
      })) === null
    )
      return;
    setErr(null);
    setBusy(true);
    try {
      const { slug, ...fields } = payload(form);
      const r =
        into === 'target' && target
          ? await api<{ item: Item }>(`/faq/items/${target.id}`, {
              method: 'PATCH',
              body: { ...fields, slug, status, version: target.version, fromTicket: ticketId },
            })
          : await api<{ item: Item }>('/faq/items', {
              body:
                into === 'new-wording' && target
                  ? { ...fields, status: 'draft', replaces: target.id, fromTicket: ticketId }
                  : { ...fields, slug, status, fromTicket: ticketId },
            });
      setDone({ status, id: r.item.id, replaces: into === 'new-wording' });
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const heading = (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-stone">
        {n} of {of}
      </span>
      {target ? <Pill tone="sky">Improves an existing answer</Pill> : <Pill>New answer</Pill>}
    </span>
  );

  if (done === 'skipped')
    return (
      <div className="flex items-center gap-3 rounded-[16px] border border-dashed border-hairline px-4 py-3 text-[14px] text-stone">
        <span className="min-w-0 flex-1 truncate">Skipped: {draft.question}</span>
        <button
          type="button"
          className="shrink-0 font-semibold text-ink underline underline-offset-2"
          onClick={() => setDone(null)}
        >
          Undo
        </button>
      </div>
    );
  if (done)
    return (
      <Card title={heading}>
        <p className="flex items-start gap-2 text-[14px] text-graphite">
          <Check size={16} className="mt-0.5 shrink-0 text-mint-deep" aria-hidden="true" />
          <span>
            <span className="font-semibold text-ink">{tidyFaqText(form.question)}</span>{' '}
            {done.status === 'published'
              ? 'is published. The site shows it within a few minutes.'
              : done.replaces
                ? `is saved as new wording for #${target?.slug}. The answer on the site stays as it is until you replace it from the draft.`
                : 'is saved as a draft.'}
          </span>
        </p>
        <Link
          to={`/faq?item=${done.id}`}
          className="mt-3 inline-flex min-h-10 items-center gap-1.5 text-[14px] font-semibold text-ink underline decoration-hairline decoration-2 underline-offset-4 hover:decoration-ink"
        >
          Open it in the FAQ <ArrowRight size={14} aria-hidden="true" />
        </Link>
      </Card>
    );

  return (
    <Card title={heading}>
      <div className="space-y-5">
        {draft.reason && (
          <p className="text-[14px] leading-relaxed text-graphite">{draft.reason}</p>
        )}
        {draft.warnings.length > 0 && (
          <ul className="space-y-1 rounded-[12px] border border-sun/60 bg-sun/15 px-3.5 py-2.5 text-[13px] font-semibold text-ink">
            {draft.warnings.map((w) => (
              <li key={w} className="flex items-start gap-2">
                <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                {w}
              </li>
            ))}
          </ul>
        )}
        {target && (
          <section className="rounded-[12px] border border-hairline bg-canvas px-3.5 py-3">
            <h3 className="mb-2 text-[13px] font-semibold text-graphite">
              Changes to the answer {live ? 'on the site' : 'in drafts'} (#{target.slug})
            </h3>
            <Changes
              from={{ q: target.question, a: target.answer }}
              to={{ q: tidyFaqText(form.question), a: tidyFaqText(form.answer) }}
            />
          </section>
        )}
        <AnswerFields
          form={form}
          set={setForm}
          groups={groups}
          autoSlug={!target && !touchedSlug(form, initial)}
          slugLocked={!!target}
          {...(target ? { slugNote: 'Saved into the existing answer, under its anchor.' } : {})}
          showAll={tried}
        />
        {err && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-[10px] bg-berry/[0.06] px-3 py-2 text-[13px] font-semibold text-berry-deep"
          >
            <CircleAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
            {err}
          </p>
        )}
        {/* Claude wrote it, so keeping it as a draft comes first; publishing asks again. */}
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
          <button
            type="button"
            className="btn-primary h-11 py-0"
            disabled={busy}
            // A live answer can't become a draft without leaving the site: the new wording waits
            // as a draft of its own that replaces it later.
            onClick={() => void save('draft', live ? 'new-wording' : target ? 'target' : 'new')}
          >
            Save as draft
          </button>
          <button
            type="button"
            className="btn-ghost h-11 py-0"
            disabled={busy}
            onClick={() => void save('published', target ? 'target' : 'new')}
          >
            {live ? 'Publish changes' : 'Publish'}
          </button>
          <button
            type="button"
            className="ml-auto inline-flex h-11 items-center rounded-[10px] px-3 text-[14px] font-semibold text-stone transition hover:bg-canvas hover:text-ink"
            onClick={() => setDone('skipped')}
            disabled={busy}
          >
            Skip
          </button>
        </div>
        {live && (
          <p className="-mt-2 text-[12px] text-stone">
            “Save as draft” keeps the answer on the site as it is. Open the draft from the FAQ page
            to replace it when the wording is right.
          </p>
        )}
      </div>
    </Card>
  );
}
